/**
 * PriDrawer — Main slide-in drawer for the pRI AI assistant.
 * 380px wide right-side drawer on desktop, fullscreen on mobile.
 * ADR-070 Precision Glass: backdrop-blur + slate palette.
 * Follows the HelpdeskDrawer pattern exactly.
 */
import { usePriStore } from '@/stores/pri-store';
import { useTranslation } from '@kgc/i18n';
import { useBreakpoint } from '@kgc/ui';
import { useEffect, useRef } from 'react';
import { PriInput } from './PriInput';
import { PriMessageList } from './PriMessageList';
import { PriWelcome } from './PriWelcome';

/** Drawer width in px (desktop only; mobile = 100vw) */
const DRAWER_WIDTH = 380;

interface PriDrawerProps {
  onClose: () => void;
}

export function PriDrawer({ onClose }: PriDrawerProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const backdropRef = useRef<HTMLDivElement>(null);
  const messages = usePriStore(s => s.messages);
  const clearHistory = usePriStore(s => s.clearHistory);

  const showWelcome = messages.length === 0;

  // ESC to close
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Backdrop click closes
  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === backdropRef.current) onClose();
  }

  const drawerWidth = isMobile ? '100vw' : DRAWER_WIDTH + 'px';

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        data-testid="pri-drawer-panel"
        className="absolute right-0 top-0 flex h-full flex-col bg-[#0f172a] shadow-2xl"
        style={{ width: drawerWidth }}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b border-slate-700/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-400 text-sm">
              🤖
            </div>
            <h2 className="text-sm font-semibold text-white">{t('priDrawer.title')}</h2>
          </div>
          <div className="flex items-center gap-1">
            {/* Clear history button */}
            {messages.length > 0 && (
              <button
                onClick={clearHistory}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-700 hover:text-white"
                title={t('priDrawer.clearHistory')}
              >
                {/* Trash icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
            {/* Close button — larger hit area for mobile */}
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-lg text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
              aria-label={t('priDrawer.close')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        {showWelcome ? <PriWelcome /> : <PriMessageList />}

        {/* ── Input ── */}
        <PriInput />
      </div>
    </div>
  );
}
